import { Controller } from "@hotwired/stimulus";
import loadImage from "blueimp-load-image";
import stampImage from "../../images/numberplate.svg";
import activeStampImage from "../../images/numberplate-active.svg";
import { StampCanvas } from "../../shared/stamp_canvas";
import Croppie from "croppie";

let idGenerator = 1;

/** mypage_listing_car_images/edit と gallery の各画像の form
 *
 * form の状態によって `data-controller` がついている要素の `data-form-status` の値が変わる。
 * - `data-form-status="normal"`: 通常状態
 * - `data-form-status="croppie"`: 画像クロップ。ダイアログ初期状態。
 * - `data-form-status="stamp"`: ナンバープレート隠し。ダイアログの中身が変わる。
 * - `data-form-status="preview"`: ファイルを選択して保存前のプレビュー状態。ダイアログが開いている。
 * - `data-form-status="loading"`: 主に保存ダイアログが閉じて画像のアップロード中。選択したファイルの読み込み中もこれ。
 *
 * data-controller="mypage-listing-car-images-form"
 */
export default class extends Controller {
  static values = {
    attached: Boolean,
    // "replace" (default: placeholder 差し替え) | "remove" (DOM から削除) | "reload" (ページ全体リロード)
    destroyMode: { type: String, default: "replace" },
    // マルチアングル撮影の該当ショットへの遷移先。feature off や固定枠以外では未設定。
    captureUrl: String,
  };
  static targets = [
    "originalImage",
    "form",
    "figure",
    "previewImage",
    "croppieContainer",
    "fileInput",
    "saveModal",
    "stampCanvasParent",
    "cropInput",
    "sizeInput",
    "resetBtn",
    "label",
    "warning",
  ];

  connect() {
    /** @type {HTMLFormElement} */
    const form = this.formTarget;

    /* reg_suit 用 data-testid の設定 */
    const prefix = "label-connector-";
    const id = `${prefix}${idGenerator++}`;

    /** @type {HTMLLabelElement[]} */
    const labels = this.labelTargets;
    labels.forEach((label) => {
      label.dataset.testid = id;
    });

    /* form挙動制御`Listener`群 */
    this.alertUnloadHandler = (event) => {
      this.#alertUnload(event);
    };
    window.addEventListener("beforeunload", this.alertUnloadHandler);
    form.addEventListener("blob-upload-form:start", () => {
      this.#loading();
    });
    form.addEventListener("blob-upload-form:success", (event) => {
      this.#update(event);
    });
    form.addEventListener("blob-upload-form:error", (event) => {
      this.error(event);
    });

    /* 車両画像編集`Listener`群 */
    this.fileInputTarget.addEventListener("change", (_event) => {
      this.#afterFileSelect();
    });
    this.cropInputTarget.addEventListener("input", (event) => {
      this.#zoomControl(event);
    });
    $(this.saveModalTarget).on("hide.bs.modal", () => {
      this.#onHideSaveModal();
    });
    this.element.dataset.formStatus = "normal";
  }

  disconnect() {
    if (this.alertUnloadHandler) {
      window.removeEventListener("beforeunload", this.alertUnloadHandler);
    }
  }

  /* - public methods - */

  /* -- 画像切り抜き -- */

  async setCroppie() {
    const blob = await this.#getCroppieResult();
    this.currentBlob = blob;
    await this.#setBlobUrl(blob);
    this.element.dataset.formStatus = "preview";
  }

  /* -- ナンバー隠し -- */

  async stamp() {
    // `currentBlob` が存在しない場合
    if (!this.currentBlob) {
      this.currentBlob = await this.#getCroppieResult();
    }
    // 編集途中の `currentBlob` が存在する場合
    await this.#setCanvasWithStamp(this.currentBlob);
    this.element.dataset.formStatus = "stamp";
  }

  async setStamp() {
    this.stampCanvas.drawResult();
    try {
      const ctb = await canvasToBlobAsync(
        this.stampCanvas.canvas,
        "image/jpeg",
        0.85,
      );
      this.currentBlob = ctb;
      await this.#setBlobUrl(ctb);
      this.element.dataset.formStatus = "preview";
    } catch (error) {
      alert("スタンプ処理中にエラーが発生しました: " + error.message);
    }
  }

  async editStamp() {
    // 車両画像が登録されていない場合
    if (!this.attachedValue) {
      // マルチアングル撮影の該当ショットへ遷移させる（feature on の固定枠のみ）
      if (this.captureUrlValue) {
        window.location.assign(this.captureUrlValue);
        return;
      }
      // ファイル選択イベント発火
      const event = new MouseEvent("click");
      this.fileInputTarget.dispatchEvent(event);
      return;
    }

    // 車両画像がすでに登録されている場合
    // 新規アップロードの保存後も選択したファイルが残るので、reset() が
    // 登録済み車両画像の編集として扱えるように破棄する。
    this.file = undefined;
    const blob = await urlToBlobAsync(this.originalImageTarget.src);
    await this.#setCanvasWithStamp(blob);

    $(this.saveModalTarget)
      .modal({
        backdrop: "static",
        keyboard: false,
      })
      .modal("show");
    this.element.dataset.formStatus = "stamp";
  }

  /* -- プレビュー -- */

  /**
   * 車両画像登録時処理 (ただし、実際の登録は `blob_upload_form.js`)
   */
  async save() {
    // #onHideSaveModal が「保存フロー」を識別してアップロード用 blobUrl を
    // revoke しないための専用フラグ。formStatus="loading" はファイル読み込み中や
    // タイムアウト縮退時にも立つため、保存フローの識別には使えない。
    this.saving = true;
    this.attachedValue = true;
    this.element.dataset.formStatus = "loading";
    $(this.saveModalTarget).modal("hide");
    if (this.hasWarningTarget) {
      this.warningTarget.classList.add("d-none");
    }
  }

  /**
   * 画像編集リセット処理 (1つ前の工程へ戻る)
   */
  async reset() {
    if (this.file) {
      // ファイル選択されている場合、クロップに遷移
      const ready = await this.#createCroppieBlob(this.file);
      // 初期化に失敗/中断した場合は "croppie" へ遷移させない。
      if (ready) {
        this.element.dataset.formStatus = "croppie";
      }
    } else if (this.element.dataset.formStatus === "stamp") {
      // 登録済み車両画像の編集ではナンバー隠しが最初の工程で戻る先が無いので、
      // 編集をキャンセルしてモーダルを閉じる (#onHideSaveModal が保存済み画像の表示に戻す)。
      $(this.saveModalTarget).modal("hide");
      return;
    } else {
      // 登録済み車両画像の編集でプレビューから戻る場合、ナンバー隠しに遷移
      const blob = await urlToBlobAsync(this.originalImageTarget.src);
      await this.#setCanvasWithStamp(blob);
      this.element.dataset.formStatus = "stamp";
    }
    this.currentBlob = undefined;
  }

  /* -- 一覧 -- */

  destroy(event) {
    // サーバ側で position が詰め直されるジャンルでは、残ったカードの hidden
    // position と DB の値がズレる。次の更新/削除で別レコードを誤操作しないよう
    // ページをリロードして状態を同期する。
    if (this.destroyModeValue === "reload") {
      window.location.reload();
      return;
    }
    const [response, ,] = event.detail;
    if (response && response.url) {
      this.originalImageTarget.src = response.url;
    }
    this.attachedValue = false;
    this.element.classList.remove("saved");
    if (this.destroyModeValue === "remove") {
      this.element.remove();
    }
  }

  error(event) {
    alert(`エラーが発生しました: ${event.detail}`);

    // バリデーションに引っかかった場合は 200 を返して update に行くので、
    // それ以外のなにかエラーが起きた。リロードしておく。
    location.reload();
  }

  /* - private methods - */

  /* -- 車両画像編集関連機能 -- */

  /**
   * 画像ファイル選択時処理
   */
  async #afterFileSelect() {
    const fileInput = this.fileInputTarget;
    if (fileInput.files.length < 1) {
      return;
    }
    this.file = fileInput.files[0];
    this.element.dataset.formStatus = "loading";

    // 同じファイルを選択した時にまた change イベントが来るように、ファイルの選択を解除する。
    fileInput.value = "";

    // 画像を読み込んでから modal を開く
    $(this.saveModalTarget)
      .modal({
        backdrop: "static",
        keyboard: false,
      })
      .modal("show");

    // croppie に file を読み込み、4:3の画像 blob を生成
    const ready = await this.#createCroppieBlob(this.file);
    // 初期化に失敗/中断した場合 (タイムアウトでモーダルを閉じた等) は
    // "croppie" へ遷移させず、UI 状態を不整合にしない。
    if (ready) {
      this.element.dataset.formStatus = "croppie";
    }
  }

  /**
   * croppie のカスタム Zoom スライダーイベント処理
   * @param {Event} event
   */
  #zoomControl(event) {
    const val = parseFloat(event.target.value);
    this.croppie.setZoom(this.minZoom + val);
  }

  /**
   * 任意の blob を croppie によってクロップされたバイナリに変換する。
   * 画像ロード〜Croppie 初期化 (または中断) までを待てるよう Promise を返す。
   * 呼び出し側はこの解決を待ってから "croppie" 状態へ遷移すること。解決前に
   * 遷移すると、待機中にモーダルを閉じた/タイムアウトしたケースで UI 状態が
   * 不整合になる。
   * @param {Blob} blob
   * @returns {Promise<boolean>} Croppie を初期化できたら true、中断・失敗した場合は false
   */
  #createCroppieBlob(blob) {
    // #waitForFigureLayout の待機中にモーダルが閉じられたり別の画像読み込みが
    // 始まったりした場合に、陳腐化した onload を無効化するための世代トークン。
    const generation = (this.croppieGeneration =
      (this.croppieGeneration ?? 0) + 1);

    // 既存 croppie 削除
    if (this.croppie) {
      this.croppie.destroy();
      this.croppie = undefined;
    }
    const img = new Image();
    const blobUrl = URL.createObjectURL(blob);
    this.fileInputTarget.dataset.src = blobUrl;
    img.src = blobUrl;

    return new Promise((resolve) => {
      img.onerror = () => {
        // 画像の読み込みに失敗した場合は Croppie を生成できない。無言で
        // Loading 状態に固定させず、通知してモーダルを閉じ再操作を促す。
        console.error(
          "[mypage-listing-car-images-form] 画像の読み込みに失敗しました",
        );
        this.#failCroppieInit();
        resolve(false);
      };

      img.onload = async () => {
        try {
          // croppie 生成
          const isLandscape = img.width >= (img.height * 4) / 3;
          // 向きによって figure の aspect-ratio が切り替わるので、レイアウトを
          // 待つ前に確定させておく。
          this.element.dataset.previewImageOrientation = isLandscape
            ? "landscape"
            : "portrait";

          // モーダルのスライドイン/フェードインアニメーション中は figure の
          // レイアウトが未確定で clientHeight/clientWidth が 0 になる。0 のまま
          // Croppie を生成すると boundary 高さが 0 になり切り抜き領域が潰れて
          // 画像が表示されず真っ白になるため、レイアウト確定を待ってから生成する。
          // (待機判定は clientHeight のみ。同一 figure 要素のため高さが確定すれば
          //  clientWidth も確定している。)
          const layoutReady = await this.#waitForFigureLayout();

          // 待機中にモーダルが閉じられた / 別の画像読み込みが始まった場合は、
          // 破棄済みコンテナへの再生成を避けるため中断する。
          if (generation !== this.croppieGeneration) {
            resolve(false);
            return;
          }

          // レイアウトが確定しないままタイムアウトした場合、高さ 0 で生成すると
          // 再び真っ白になる。無言で縮退させず、生成を中止して再操作を促す。
          if (!layoutReady) {
            console.error(
              "[mypage-listing-car-images-form] figure のレイアウトが確定せず Croppie を初期化できません",
              { clientHeight: this.figureTarget.clientHeight, isLandscape },
            );
            this.#failCroppieInit();
            resolve(false);
            return;
          }

          const vh = isLandscape
            ? this.figureTarget.clientHeight
            : (this.figureTarget.clientWidth * 3) / 4;
          const vw = isLandscape
            ? (this.figureTarget.clientHeight * 4) / 3
            : this.figureTarget.clientWidth;
          this.minZoom = isLandscape ? vh / img.height : vw / img.width;
          this.croppieContainerTarget.dataset.blobUrl = blobUrl;

          this.croppie = new Croppie(this.croppieContainerTarget, {
            viewport: {
              width: vw,
              height: vh,
              type: "square",
            },
            showZoomer: false,
            mouseWheelZoom: false,
            boundary: {
              height: this.figureTarget.clientHeight,
            },
            minZoom: this.minZoom,
            maxZoom: this.minZoom + 1.5,
          });

          this.croppie.bind({ url: blobUrl, zoom: 0 });
          resolve(true);
        } catch (error) {
          // Croppie 初期化中の想定外例外。onerror/timeout と同様に、Loading 状態で
          // 固まらないよう通知してモーダルを閉じる。
          console.error(
            "[mypage-listing-car-images-form] Croppie の初期化に失敗しました",
            error,
          );
          this.#failCroppieInit();
          resolve(false);
        }
      };
    });
  }

  /**
   * Croppie 初期化に失敗したときの縮退処理。
   * モーダルは backdrop:static / keyboard:false でユーザーが自力で閉じられず、
   * status="loading" のまま放置すると Loading スピナーで固まってしまうため、
   * 通知してモーダルを閉じ (→ #onHideSaveModal で normal に戻る) 再操作を促す。
   */
  #failCroppieInit() {
    alert(
      "画像編集画面の初期化に失敗しました。お手数ですが、もう一度画像を選び直してください。",
    );
    $(this.saveModalTarget).modal("hide");
  }

  /**
   * クロップ枠 (figure) のレイアウトが確定して高さを持つまで待つ。
   * モーダルの表示アニメーション中は clientHeight が 0 になるため、高さが
   * 入るまで setTimeout でポーリングする (50ms × 最大 40 回 = 約 2 秒)。
   * requestAnimationFrame は非アクティブタブ (WebView がバックグラウンドの
   * 場合等) で発火が止まりタイムアウトに到達できないため使わない。
   * @returns {Promise<boolean>} 高さが確定したら true、タイムアウトしたら false
   */
  #waitForFigureLayout() {
    return new Promise((resolve) => {
      let attempts = 0;
      const check = () => {
        if (this.figureTarget.clientHeight > 0) {
          resolve(true);
          return;
        }
        if (attempts >= 40) {
          resolve(false);
          return;
        }
        attempts += 1;
        setTimeout(check, 50);
      };
      check();
    });
  }

  /**
   * form の送信用の blobUrl を設定する
   * @param {Blob} blob
   */
  async #setBlobUrl(blob) {
    const blobUrl = URL.createObjectURL(blob);
    this.previewImageTarget.src = blobUrl;
    const fileInput = this.fileInputTarget;
    if (fileInput.dataset.blobUrl) {
      URL.revokeObjectURL(fileInput.dataset.blobUrl);
    }
    fileInput.dataset.blobUrl = blobUrl;
  }

  /**
   * クロップ画像から車両ナンバーを隠した画像を生成する
   * @param {Blob} blob
   */
  async #setCanvasWithStamp(blob) {
    const canvasWidth = this.element.dataset.imageMaxWidth;
    const canvasHeight = this.element.dataset.imageMaxHeight;

    const { image: canvas } = await loadImage(blob, {
      maxWidth: canvasWidth,
      maxHeight: canvasHeight,
      canvas: true,
    });
    const stamp = await createImage(stampImage);
    const uiStamp = await createImage(activeStampImage);

    // ナンバープレートのサイズは 110x55 だけど、斜め向きだと高さが狭すぎるので大きめにする。
    this.stampCanvas = new StampCanvas(
      canvas,
      canvasWidth,
      canvasHeight,
      stamp,
      uiStamp,
      165,
      105,
    );
    this.stampCanvas.stampX = canvasWidth / 2;
    this.stampCanvas.stampY = canvasHeight / 2;
    this.stampCanvas.drawUi();

    // sizeInput と stampCanvas をつなげる。
    const sizeInput = this.sizeInputTarget;
    sizeInput.value = this.stampCanvas.stampScale;
    sizeInput.addEventListener("input", () => {
      this.stampCanvas.stampScale = parseFloat(sizeInput.value);
      this.stampCanvas.drawUi();
    });
    this.stampCanvas.onChangeScale = (scale) => {
      sizeInput.value = scale;
    };

    // 中身を置き換える。
    while (this.stampCanvasParentTarget.firstChild) {
      this.stampCanvasParentTarget.removeChild(
        this.stampCanvasParentTarget.firstChild,
      );
    }
    this.stampCanvasParentTarget.append(this.stampCanvas.canvas);
  }

  /**
   * クロップ画像の結果を取得する
   * @returns {Blob}
   */
  async #getCroppieResult() {
    if (!this.croppie) return null;
    const width = this.element.dataset.imageMaxWidth;
    const height = this.element.dataset.imageMaxHeight;
    return await this.croppie.result({
      type: "blob",
      size: { width, height },
      format: "jpeg",
      quality: 0.85,
    });
  }

  #onHideSaveModal() {
    /** @type {HTMLFormElement} */
    const form = this.formTarget;

    // save() が立てた保存フラグ。保存フローでは fileInput.dataset.blobUrl を
    // この後の blob-upload-form#submit がアップロードに使うので revoke しない。
    // モーダルを閉じるたびにフラグを下ろす。
    const isSaving = this.saving === true;
    this.saving = false;

    form.reset();
    // 待機中 (#waitForFigureLayout) の初期化を陳腐化させ、破棄後に onload が
    // 解決しても Croppie を再生成しないようにする。
    this.croppieGeneration = (this.croppieGeneration ?? 0) + 1;
    if (this.croppie) {
      this.croppie.destroy();
      this.croppie = undefined;
    }

    // URL.createObjectURL で生成した ObjectURL を解放してメモリリークを防ぐ。
    // croppie 用の URL (fileInput.dataset.src / croppieContainer.dataset.blobUrl)
    // はアップロードに使わないので常に解放する。
    this.#revokeDatasetObjectUrl(this.fileInputTarget, "src");
    this.#revokeDatasetObjectUrl(this.croppieContainerTarget, "blobUrl");
    // プレビュー/アップロード用 URL (fileInput.dataset.blobUrl) は保存フローで
    // 必要なため、キャンセルで閉じた場合のみ解放する。保存時は
    // blob-upload-form 側がアップロード完了後に解放する。
    if (!isSaving) {
      this.#revokeDatasetObjectUrl(this.fileInputTarget, "blobUrl");
    }

    this.currentBlob = undefined;
    this.element.dataset.formStatus = "normal";
    this.element.dataset.previewImageOrientation = "";
  }

  /**
   * dataset に保持した ObjectURL を revoke して該当エントリを削除する。
   * @param {HTMLElement} element
   * @param {string} key dataset のキー (例: "src", "blobUrl")
   */
  #revokeDatasetObjectUrl(element, key) {
    const url = element.dataset[key];
    if (!url) return;

    URL.revokeObjectURL(url);
    delete element.dataset[key];
  }

  /* -- form挙動制御関連 -- */

  #alertUnload(event) {
    // preview or 通信状態で画面遷移しそうになったら止める。
    if (["loading", "preview"].includes(this.element.dataset.formStatus)) {
      event.preventDefault();
      event.returnValue = "";
    }
  }

  #loading() {
    this.element.dataset.formStatus = "loading";
  }

  #update(event) {
    const [response, ,] = event.detail;

    if (response.errors) {
      this.element.dataset.formStatus = "preview";
      alert("画像を保存できませんでした。\n" + response.errors.join("\n"));
      return;
    }
    this.element.dataset.formStatus = "normal";
    this.element.classList.add("saved");

    this.originalImageTarget.src = response.url;

    const form = event.target;
    form.reset();
    // 何故か disabled になっていたので戻す。
    Array.from(form.elements).forEach((x) => {
      x.disabled = false;
    });
  }
}

/* - utilities function - */

/**
 * @param {HTMLCanvasElement} canvas
 * @param {String} mimeType
 * @param {Number} [qualityArgument]
 * @returns {Promise<Blob>}
 */
function canvasToBlobAsync(canvas, mimeType, qualityArgument) {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("画像の処理に失敗しました"));
          }
        },
        mimeType,
        qualityArgument,
      );
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * @returns {Promise<HTMLImageElement>}
 */
function createImage(src) {
  const image = new Image();
  return new Promise((resolve, _reject) => {
    image.addEventListener("load", () => {
      resolve(image);
    });
    image.src = src;
  });
}

/**
 * @param {String} url
 * @returns {Promise<Blob>}
 */
async function urlToBlobAsync(url) {
  const res = await fetch(url);
  return await res.blob();
}
